import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import { useDemoMode } from './useDemoMode';
import API_CONFIG from '../config/api';

export const useAuth = () => {
  const { isDemoMode } = useDemoMode();
  const { instance, accounts, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Demo mode mock authentication - moved hooks calls outside of conditional
  const demoAuthReturn = {
    isAuthenticated: true,
    accessToken: 'demo-token',
    loading: false,
    accounts: [{ username: 'demo-user@example.com' }],
    getAccessToken: async () => 'demo-token',
    makeAuthenticatedRequest: async (url, options = {}) => {
      // Convert API_CONFIG.BASE_URL (/api or http://localhost:8000/api) to demo endpoint
      const baseUrl = API_CONFIG.BASE_URL; // e.g., "http://localhost:8000/api" or "/api"
      const demoBaseUrl = baseUrl.replace('/api', '/api/demo'); // e.g., "http://localhost:8000/api/demo" or "/api/demo"
      const demoUrl = url.replace(baseUrl, demoBaseUrl);
      console.log('Demo mode: Making request to:', demoUrl);
      
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      return fetch(demoUrl, {
        ...options,
        headers,
      });
    },
    acquireToken: async () => {},
    clearCache: async () => {},
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // Wait for MSAL to finish any in-progress operations
      if (inProgress !== 'none') {
        return;
      }

      console.log('Initializing auth, accounts:', accounts.length);
      
      if (accounts.length > 0) {
        console.log('Found accounts in accounts array:', accounts[0].username);
        setIsAuthenticated(true);
        await acquireToken();
      } else {
        // Check if there are any cached accounts that haven't been loaded yet
        const allAccounts = instance.getAllAccounts();
        console.log('Checking cached accounts:', allAccounts.length);
        
        if (allAccounts.length > 0) {
          console.log('Found cached account:', allAccounts[0].username);
          // Set active account if not already set
          if (!instance.getActiveAccount()) {
            instance.setActiveAccount(allAccounts[0]);
          }
          setIsAuthenticated(true);
          await acquireToken();
        } else {
          console.log('No accounts found, user needs to sign in');
          setIsAuthenticated(false);
          setAccessToken(null);
          setLoading(false);
        }
      }
      
      setInitialized(true);
    };

    // Skip MSAL authentication entirely in demo mode
    if (isDemoMode) {
      console.log('Demo mode detected, skipping MSAL authentication');
      return;
    }

    // Always run auth check when accounts change or when not yet initialized
    if (!initialized || accounts.length > 0) {
      initializeAuth();
    }
  }, [accounts, instance, inProgress, initialized, isDemoMode]);

  const acquireToken = async () => {
    try {
      // Get account from accounts array or active account
      const account = accounts[0] || instance.getActiveAccount();
      
      if (!account) {
        console.log('No account available for token acquisition');
        setLoading(false);
        return;
      }

      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      
      setAccessToken(response.accessToken);
      setLoading(false);
      console.log('Token acquired silently for user:', account.username);
    } catch (error) {
      console.error('Silent token acquisition failed:', error);
      
      // Only try interactive acquisition if it's an interaction required error
      if (error.name === 'InteractionRequiredAuthError') {
        console.log('Interaction required, but skipping popup to avoid unnecessary dialogs');
      }
      
      setAccessToken(null);
      setLoading(false);
    }
  };

  const getAccessToken = async () => {
    if (accessToken) {
      return accessToken;
    }
    
    if (accounts.length > 0) {
      try {
        const account = accounts[0];
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account,
        });
        
        setAccessToken(response.accessToken);
        return response.accessToken;
      } catch (error) {
        console.error('Silent token acquisition failed:', error);
        try {
          // Try interactive token acquisition
          const response = await instance.acquireTokenPopup(loginRequest);
          setAccessToken(response.accessToken);
          return response.accessToken;
        } catch (interactiveError) {
          console.error('Interactive token acquisition failed:', interactiveError);
          return null;
        }
      }
    }
    
    return null;
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    console.log('Making authenticated request to:', url);
    console.log('Current access token available:', !!accessToken);
    console.log('Accounts available:', accounts.length);
    
    const token = await getAccessToken();
    
    if (!token) {
      console.error('Failed to get access token');
      throw new Error('No access token available');
    }

    console.log('Access token acquired successfully');

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const clearCache = async () => {
    try {
      // Clear all cached tokens
      await instance.clearCache();
      setAccessToken(null);
      setIsAuthenticated(false);
      console.log('Authentication cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  };

  // Return demo authentication if in demo mode
  if (isDemoMode) {
    return demoAuthReturn;
  }

  return {
    isAuthenticated,
    accessToken,
    loading,
    accounts,
    getAccessToken,
    makeAuthenticatedRequest,
    acquireToken,
    clearCache,
  };
};